import { useCloseBankAccount } from '@/hooks/requests/useBankAccounts';
import {
  Dialog,
  DialogContent,
  Typography,
  DialogActions,
} from '@mui/material';
import { SVButton, SVLoader } from '../../core';
import showToast from '@/utils/toast';
import { MESSAGES } from '@/constants/messages';

type BankAccountCloseModalProps = {
  bankAccountId: string;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export default function BankAccountCloseModal({
  bankAccountId,
  open,
  setOpen,
}: BankAccountCloseModalProps) {
  const {
    canCloseBankAccount,
    isCanCloseBankAccountPending,
    cloesBankAccount,
    isCloseBankAccountPending,
  } = useCloseBankAccount(bankAccountId, open);

  if (isCanCloseBankAccountPending) {
    return (
      <Dialog
        fullWidth
        maxWidth="lg"
        open={open}
        onClose={() => setOpen(false)}>
        <DialogContent>
          <SVLoader />
        </DialogContent>
      </Dialog>
    );
  }

  if (!canCloseBankAccount) {
    return (
      <Dialog
        fullWidth
        maxWidth="lg"
        open={open}
        onClose={() => setOpen(false)}>
        <DialogContent>
          <Typography variant="h4">Cannot Close Bank Account</Typography>
          <Typography>
            This bank account has a balance and / or a pending transaction. To
            close a bank account, the bank account balance must be $0 and all
            transactions must be settled
          </Typography>
        </DialogContent>
        <DialogActions>
          <SVButton variant="outlined" onClick={() => setOpen(false)}>
            Back
          </SVButton>
        </DialogActions>
      </Dialog>
    );
  }

  async function handleCloseBankAccount() {
    try {
      await cloesBankAccount();
      showToast({
        type: 'success',
        data: { content: 'Bank account has been successfully closed' },
      });
      setOpen(false);
    } catch {
      showToast({ type: 'error', data: { content: MESSAGES.genericError } });
    }
  }

  return (
    <Dialog fullWidth maxWidth="lg" open={open} onClose={() => setOpen(false)}>
      <DialogContent>
        <Typography variant="h4">Close Bank Account?</Typography>
        <Typography>
          Are you sure you want to close this bank account? The bank account
          will be closed permanently and the account owners will receive a
          confirmation email that the account is closed.
        </Typography>
      </DialogContent>
      <DialogActions>
        <SVButton variant="outlined" onClick={() => setOpen(false)}>
          No, Don't Close
        </SVButton>
        <SVButton
          variant="error"
          isLoading={isCloseBankAccountPending}
          onClick={handleCloseBankAccount}>
          Yes, Close
        </SVButton>
      </DialogActions>
    </Dialog>
  );
}
